import { FETCH_LIST_START, FETCH_LIST_SUCCESS, FETCH_LIST_FAIL } from './actionTypes';
import fetchData from './fetchData';
import { translate } from '../../shared/utility';
import { getLocale } from '../../shared/translations';

// object that holds properties to store total count of requests,
// for each request the total will be incremented by 1,
// when final request finishes total will be reset to 0
const count = {
    itemsTotal: 0,
    mainTypesTotal: 0,
    itemTypesTotal: 0,
    nearbyItemsTotal: 0,
    popularLocationsTotal: 0,
    searchAutocompleteTotal: 0,
    reservationsTotal: 0,
    ordersTotal: 0,
    orderReservationsTotal: 0,
    addressesTotal: 0,
    countriesTotal: 0,
    paymentAccountsTotal: 0,
    paymentMethodsTotal: 0,
    paymentOptionsTotal: 0,
    directDebitsTotal: 0

};

/* * * * * * * * * * * * * * *
* ACTIVE ACTION TYPE METHODS *
* * * * * * * * * * * * * *  */
// action type methods return current active action type that is determined by the state of the fetch requests.
// Also these methods pass data passed from user methods to Redux reducers to update states
export const fetchListStart = (identifier) => {
    return {
        type: FETCH_LIST_START,
        identifier: identifier
    };
};

export const fetchListSuccess = (identifier, data = [], concat = false) => {
    return {
        type: FETCH_LIST_SUCCESS,
        identifier: identifier,
        data: data,
        concat: concat
    };
};

export const fetchListFail = (identifier, error = 'Error message missing. Please contact site administrator.') => {
    return {
        type: FETCH_LIST_FAIL,
        identifier: identifier,
        error: error
    };
};

/* * * * * * * * * * * *  *
* USER ACCESSIBLE METHODS *
* * * * * * * * * * * * * */
// User accessible methods are exported and can be accessed across the whole project.
// These methods execute fetch calls and dispatch correct method that updates active action type according the state of the request

/* * * * * * *  *
* LIST OF TYPES *
* * * * * * * * */
export const fetchMainTypes = () => {
    const current = count.mainTypesTotal + 1;
    count.mainTypesTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('mainTypes'));
        try {
            const data = await fetchData('GET', 'mainItemTypes');
            if (current === count.mainTypesTotal) {
                count.mainTypesTotal = 0;
                dispatch(fetchListSuccess('mainTypes', data));
            }
        } catch (error) {
            if (current === count.mainTypesTotal) {
                count.mainTypesTotal = 0;
                dispatch(fetchListFail('mainTypes', error));
            }
        }
    };
};

export const fetchItemTypes = () => {
    const current = count.itemTypesTotal + 1;
    count.itemTypesTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('itemTypes'));
        try {
            const data = await fetchData('GET', 'itemTypes');
            if (current === count.itemTypesTotal) {
                count.itemTypesTotal = 0;
                dispatch(fetchListSuccess('itemTypes', data));
            }
        } catch (error) {
            if (current === count.itemTypesTotal) {
                count.itemTypesTotal = 0;
                dispatch(fetchListFail('itemTypes', error));
            }
        }
    };
};

/* * * * * * *  *
* LIST OF ITEMS *
* * * * * * * * */
export const fetchItems = (mainTypeIds, itemTypeIds, location = { lat: null, lng: null }, page = { number: 1, size: 20 }, filters = {}) => {
    const locationStr = location ? `&latitude=${location.lat}&longitude=${location.lng}&range=${10000}` : '';
    const mainTypeIdStr = mainTypeIds && !itemTypeIds.length ? `&mainItemTypeIds=${mainTypeIds}` : '';
    const itemTypeIdsStr = itemTypeIds.length ? itemTypeIds.map(typeId => `&itemTypeIds=${typeId}`).join('') : '';
    const extraFiltersStr = Object.keys(filters).length ? '&' + Object.entries(filters).map(map => `${map[0]}=${map[1]}`).join('&') : '';

    const current = count.itemsTotal + 1;
    count.itemsTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('items'));
        try {
            const data = await fetchData('GET', `items?pageNumber=${page.number}&pageSize=${page.size}${locationStr}${mainTypeIdStr}${itemTypeIdsStr}${extraFiltersStr}`);
            if (current === count.itemsTotal) {
                count.itemsTotal = 0;
                dispatch(fetchListSuccess('items', data, page.number > 1));
            }
        } catch (error) {
            if (current === count.itemsTotal) {
                count.itemsTotal = 0;
                dispatch(fetchListFail('items', error));
            }
        }
    };
};

/* * * * * * * * * * * *
* LIST OF NEARBY ITEMS *
* * * * * * * * * * *  */
export const fetchNearbyItems = (location = { lat: null, lng: null }, page = { number: 1, size: 20 }) => {
    const current = count.nearbyItemsTotal + 1;
    count.nearbyItemsTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('nearbyItems'));
        try {
            const data = await fetchData('GET', `items?pageNumber=${page.number}&pageSize=${page.size}&latitude=${location.lat}&longitude=${location.lng}&range=${10000}`);
            if (current === count.nearbyItemsTotal) {
                count.nearbyItemsTotal = 0;
                dispatch(fetchListSuccess('nearbyItems', data));
            }
        } catch (error) {
            if (current === count.nearbyItemsTotal) {
                count.nearbyItemsTotal = 0;
                dispatch(fetchListFail('nearbyItems', error));
            }
        }
    };
};

/* * * * * * * * *  *
* LIST OF LOCATIONS *
* * * * * * * * * * */
export const fetchPopularLocations = (page = { number: 1, size: 20 }) => {
    const current = count.popularLocationsTotal + 1;
    count.popularLocationsTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('popularLocations'));
        try {
            const data = await fetchData('GET', `locations?pageNumber=${page.number}&pageSize=${page.size}`);
            if (current === count.popularLocationsTotal) {
                count.popularLocationsTotal = 0;
                dispatch(fetchListSuccess('popularLocations', data));
            }
        } catch (error) {
            if (current === count.popularLocationsTotal) {
                count.popularLocationsTotal = 0;
                dispatch(fetchListFail('popularLocations', error));
            }
        }
    };
};

/* * * * * * * * * * * * * * * * * * * *
* LIST OF SEARCH AUTOCOMPLETE LOCATION *
* * * * * * * * * * * * * * * * * * *  */
export const fetchSearchAutocomplete = (search = null, locale = 'nl-NL', global = false) => {
    const current = count.searchAutocompleteTotal + 1;
    count.searchAutocompleteTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('searchAutocomplete'));
        try {
            const data = await fetchData('GET', `geolocations/autocomplete?search=${search}&language=${locale}&global_search=${global}`);
            if (current === count.searchAutocompleteTotal) {
                count.searchAutocompleteTotal = 0;
                const lang = getLocale(locale).language;
                dispatch(fetchListSuccess('searchAutocomplete', !data.length ? [{ place_id: 'ZERO_RESULTS', description: translate('errors.search.noresultsAutocomplete', lang) }] : data));
            }
        } catch (error) {
            if (current === count.searchAutocompleteTotal) {
                count.searchAutocompleteTotal = 0;
                // error should be displayed as an autocomplete result item
                // dispatch(fetchListFail('searchAutocomplete', error));
                const lang = getLocale(locale).language;
                dispatch(fetchListSuccess('searchAutocomplete', [{ place_id: 'ERROR_RESULTS', description: translate('errors.search.errorAutocomplete', lang) }]));
            }
        }
    };
};

/* * * * * * * * * * * *
* LIST OF RESERVATIONS *
* * * * * * * * * * *  */
export const fetchReservations = (date = { after: null, before: null }, page = { number: 1, size: 20 }, orderDescending = false, includeCancelled = false) => {
    const endDateAfterApi = !!date.after ? `&endDateAfter=${date.after}` : '';
    const endDateBeforeApi = !!date.before ? `&endDateBefore=${date.before}` : '';

    const current = count.reservationsTotal + 1;
    count.reservationsTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('reservations'));
        try {
            const data = await fetchData('GET', `reservations?pageNumber=${page.number}&pageSize=${page.size}${endDateAfterApi}${endDateBeforeApi}&orderDescending=${orderDescending}`);
            if (current === count.reservationsTotal) {
                count.reservationsTotal = 0;
                dispatch(fetchListSuccess('reservations', data, page.number > 1));
            }
        } catch (error) {
            if (current === count.reservationsTotal) {
                count.reservationsTotal = 0;
                dispatch(fetchListFail('reservations', error));
            }
        }
    };
};

/* * * * * * * * *
* LIST OF ORDERS *
* * * * * * * *  */
export const fetchOrders = (page = { number: 1, size: 20 }) => {
    const current = count.ordersTotal + 1;
    count.ordersTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('orders'));
        try {
            const data = await fetchData('GET', `orders?pageNumber=${page.number}&pageSize=${page.size}`);
            if (current === count.ordersTotal) {
                count.ordersTotal = 0;
                dispatch(fetchListSuccess('orders', data, page.number > 1));
            }
        } catch (error) {
            if (current === count.ordersTotal) {
                count.ordersTotal = 0;
                dispatch(fetchListFail('orders', error));
            }
        }
    };
};

export const fetchOrderReservations = (orderId = null) => {
    const current = count.orderReservationsTotal + 1;
    count.orderReservationsTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('orderReservations'));
        try {
            const data = await fetchData('GET', `orders/${orderId}/reservations`);
            if (current === count.orderReservationsTotal) {
                count.orderReservationsTotal = 0;
                dispatch(fetchListSuccess('orderReservations', data));
            }
        } catch (error) {
            if (current === count.orderReservationsTotal) {
                count.orderReservationsTotal = 0;
                dispatch(fetchListFail('orderReservations', error));
            }
        }
    };
};

/* * * * * * * * *  *
* LIST OF ADDRESSES *
* * * * * * * * * * */
export const fetchAddresses = (postalCode = null, number = null) => {
    const current = count.addressesTotal + 1;
    count.addressesTotal = current;
    return !postalCode || !number ? null : async dispatch => {
        dispatch(fetchListStart('addresses'));
        try {
            const data = await fetchData('GET', `addresses?postalCode=${postalCode}&number=${number}`);
            if (current === count.addressesTotal) {
                count.addressesTotal = 0;
                dispatch(fetchListSuccess('addresses', data));
            }
        } catch (error) {
            if (current === count.addressesTotal) {
                count.addressesTotal = 0;
                dispatch(fetchListFail('addresses', error));
            }
        }
    };
};

/* * * * * * * * *  *
* LIST OF COUNTRIES *
* * * * * * * * * * */
export const fetchCountries = () => {
    const current = count.countriesTotal + 1;
    count.countriesTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('countries'));
        try {
            const data = await fetchData('GET', 'countries');
            if (current === count.countriesTotal) {
                count.countriesTotal = 0;
                dispatch(fetchListSuccess('countries', data));
            }
        } catch (error) {
            if (current === count.countriesTotal) {
                count.countriesTotal = 0;
                dispatch(fetchListFail('countries', error));
            }
        }
    };
};

/* * * * * * * * * * * * *
* FETCH PAYMENT ACCOUNTS *
* * * * * * * * * * * *  */
export const fetchPaymentAccounts = () => {
    const current = count.paymentAccountsTotal + 1;
    count.paymentAccountsTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('paymentAccounts'));
        try {
            const data = await fetchData('GET', 'paymentAccounts');
            if (current === count.paymentAccountsTotal) {
                count.paymentAccountsTotal = 0;
                dispatch(fetchListSuccess('paymentAccounts', data));
            }
        } catch (error) {
            if (current === count.paymentAccountsTotal) {
                count.paymentAccountsTotal = 0;
                dispatch(fetchListFail('paymentAccounts', error));
            }
        }
    };
};

/* * * * * * * *  *
* PAYMENT METHODS *
* * * * * * * * * */
export const fetchPaymentMethods = () => {
    const current = count.paymentMethodsTotal + 1;
    count.paymentMethodsTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('paymentMethods'));
        try {
            const data = await fetchData('GET', 'paymentMethods');
            if (current === count.paymentMethodsTotal) {
                count.paymentMethodsTotal = 0;
                dispatch(fetchListSuccess('paymentMethods', data));
            }
        } catch (error) {
            if (current === count.paymentMethodsTotal) {
                count.paymentMethodsTotal = 0;
                dispatch(fetchListFail('paymentMethods', error));
            }
        }
    };
};

/* * * * * * * * * * * *
* CART PAYMENT OPTIONS *
* * * * * * * * * * *  */
export const fetchPaymentOptions = (paymentAccountId = null) => {
    const current = count.paymentOptionsTotal + 1;
    count.paymentOptionsTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('paymentOptions'));
        try {
            const data = await fetchData('GET', `paymentAccounts/${paymentAccountId}/cart/paymentOptions`);
            if (current === count.paymentOptionsTotal) {
                count.paymentOptionsTotal = 0;
                dispatch(fetchListSuccess('paymentOptions', data));
            }
        } catch (error) {
            if (current === count.paymentOptionsTotal) {
                count.paymentOptionsTotal = 0;
                dispatch(fetchListFail('paymentOptions', error));
            }
        }
    };
};

/* * * * * * * * * * * * * * * *
* DIRECT DEBIT PAYMENT METHODS *
* * * * * * * * * * * * * * *  */
export const fetchDirectDebits = () => {
    const current = count.directDebitsTotal + 1;
    count.directDebitsTotal = current;
    return async dispatch => {
        dispatch(fetchListStart('directDebits'));
        try {
            const data = await fetchData('GET', 'directDebitPaymentMethods');
            if (current === count.directDebitsTotal) {
                count.directDebitsTotal = 0;
                dispatch(fetchListSuccess('directDebits', data));
            }
        } catch (error) {
            if (current === count.directDebitsTotal) {
                count.directDebitsTotal = 0;
                dispatch(fetchListFail('directDebits', error));
            }
        }
    };
};



// WEBPACK FOOTER //
// ./src/store/actions/list.js